/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import clsx from "clsx"
import { ChevronRightIcon } from "lucide-react"
import { observer } from "mobx-react-lite"
import { useId } from "react"

import type { ApplicationFlag } from "../../features/application-detail/application-detail-types"
import { formatTimestamp } from "../../utils/format-timestamp"
import { isNonEmptyObject } from "../../utils/is-non-empty-object"
import { valueOrDash } from "../../utils/value-or-dash"
import { Button } from "../primitives/button"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../primitives/collapsible"
import { TableCell, TableRow } from "../primitives/table"
import styles from "./flag-history-row.module.css"
import { FlagMetadata } from "./flag-metadata"
import { RiskFlag } from "./risk-flag"

interface Props {
    /**
     * One entry in the device's flag history.
     *
     * The same flag can appear more than once with different timestamps or
     * metadata.
     */
    flag: ApplicationFlag
}

/**
 * Renders one flag history entry with its creation and removal timestamps.
 *
 * Entries with metadata can expand a second row spanning the table to show it.
 * Metadata starts collapsed because it can contain many lines.
 *
 * Each entry uses its own `<tbody>` because the collapsible root must render an
 * element, and a `<div>` is not valid directly inside a table. The `<tbody>`
 * also owns the separator between entries, so the optional metadata row does
 * not add an extra border.
 *
 * @example
 * <table>
 *     {flags.map((flag) => (
 *         <FlagHistoryRow key={`${flag.name}-${flag.timestampCreated}-${flag.timestampRemoved}`} flag={flag} />
 *     ))}
 * </table>
 */
export const FlagHistoryRow = observer(({ flag }: Props) => {
    const flagId = useId()

    // Only non-empty metadata objects are shown;
    // the API can return `null` or `""` when none is available.
    const withMetadata = isNonEmptyObject(flag.metadata)

    return (
        <Collapsible render={<tbody className="border-b last:border-0" />}>
            {/* Keep the metadata row visually consistent with the expanded trigger row. */}
            <TableRow className="border-0">
                <TableCell id={flagId}>
                    <RiskFlag name={flag.name} score={flag.score} />
                </TableCell>
                <TableCell className="tabular-nums">{valueOrDash(formatTimestamp(flag.timestampCreated))}</TableCell>
                <TableCell className="tabular-nums">{valueOrDash(formatTimestamp(flag.timestampRemoved))}</TableCell>
                <TableCell className="text-right">{withMetadata && <MetadataToggle flagId={flagId} />}</TableCell>
            </TableRow>
            {withMetadata && (
                <TableRow className="border-0 bg-muted/50">
                    <TableCell colSpan={4} className="py-0 whitespace-normal">
                        <CollapsibleContent className={styles.panel}>
                            <div className={styles.metadata}>
                                <FlagMetadata flagName={flag.name} value={flag.metadata} />
                            </div>
                        </CollapsibleContent>
                    </TableCell>
                </TableRow>
            )}
        </Collapsible>
    )
})

interface MetadataToggleProps {
    /**
     * ID of the flag cell, used to include the flag name and score in the
     * toggle's accessible name.
     */
    flagId: string
}

/**
 * Toggles the metadata for one flag history entry.
 *
 * The label and chevron reflect the open state provided by the collapsible.
 * `CollapsibleTrigger` supplies `aria-expanded` and `aria-controls`.
 *
 * All toggles use the same visible label, so `aria-labelledby` also includes
 * the flag cell to distinguish them when screen readers list the buttons.
 */
const MetadataToggle = ({ flagId }: MetadataToggleProps) => {
    const id = useId()

    return (
        <CollapsibleTrigger
            id={id}
            aria-labelledby={`${id} ${flagId}`}
            render={(props, { open }) => (
                <Button {...props} variant="outline" size="xs" className="font-semibold uppercase">
                    {open ? "Hide metadata" : "Show metadata"}
                    <ChevronRightIcon
                        data-icon="inline-end"
                        className={clsx("transition-transform", { "rotate-90": open })}
                    />
                </Button>
            )}
        />
    )
}
